/**
 * DroneEngine: orchestrates the full drone pad + reverb signal chain.
 *
 * Signal flow:
 *   DronePad -> CloudReverbNode (AudioWorklet) -> Destination
 *
 * Follows the same lazy-loading pattern as MetronomeAudio.
 */

import { CloudReverbNode, type CloudReverbParams } from "./audio/cloud-reverb-node";
import { DronePad, type DronePadConfig } from "./audio/drone-pad";
import { DRONE_PRESETS, type DronePresetName } from "./audio/drone-presets";
import { getNativeAudioContext } from "./audio/get-native-context";

export interface DroneEngineConfig {
  pad: Partial<DronePadConfig>;
  reverb: Partial<CloudReverbParams>;
  preset?: DronePresetName;
}

export class DroneEngine {
  private pad: DronePad;
  private reverb: CloudReverbNode;
  private _initialized = false;
  private _preset: DronePresetName | null;

  constructor(config?: Partial<DroneEngineConfig>) {
    const preset = config?.preset ? DRONE_PRESETS[config.preset] : undefined;
    const padConfig = preset ? preset.pad : config?.pad;
    const reverbConfig = preset
      ? { ...preset.reverb, ...config?.reverb }
      : config?.reverb;

    this.pad = new DronePad(padConfig);
    this.reverb = new CloudReverbNode(reverbConfig);
    this._preset = config?.preset ?? null;
  }

  async initialize(destination?: AudioNode): Promise<void> {
    if (this._initialized) return;

    const ctx = await getNativeAudioContext();
    if (ctx.state !== "running") {
      await ctx.resume();
    }

    const padOutput = await this.pad.initialize();
    await this.reverb.load();

    if (!this.reverb.input || !this.reverb.output) {
      throw new Error("CloudReverbNode was loaded without input/output nodes");
    }
    padOutput.connect(this.reverb.input);
    this.reverb.output.connect(destination ?? ctx.destination);

    this._initialized = true;
  }

  get initialized(): boolean {
    return this._initialized;
  }

  async noteOn(note: string): Promise<void> {
    if (!this._initialized) await this.initialize();
    await this.pad.noteOn(note);
  }

  noteOff(note: string): void {
    this.pad.noteOff(note);
  }

  allNotesOff(): void {
    this.pad.allNotesOff();
  }

  setReverbParam<K extends keyof CloudReverbParams>(key: K, value: CloudReverbParams[K]): void {
    this.reverb.setParam(key, value);
  }

  setReverbParams(params: Partial<CloudReverbParams>): void {
    this.reverb.setParams(params);
  }

  /** @see DronePad.setTransitionScale */
  setTransitionScale(scale: number): void {
    this.pad.setTransitionScale(scale);
  }

  setPadVolume(db: number): void {
    this.pad.setVolume(db);
  }

  setPadFilterCutoff(hz: number): void {
    this.pad.setFilterCutoff(hz);
  }

  setPadFilterHPCutoff(hz: number): void {
    this.pad.setFilterHPCutoff(hz);
  }

  get preset(): DronePresetName | null {
    return this._preset;
  }

  async setPreset(name: DronePresetName): Promise<void> {
    const preset = DRONE_PRESETS[name];
    this._preset = name;

    await this.pad.setConfig(preset.pad);
    this.reverb.setParams(preset.reverb);
  }

  dispose(): void {
    this.pad.dispose();
    this.reverb.dispose();
    this._initialized = false;
  }
}
